/** Diálogos: compartir, fondo, plantillas, atajos y nombre de usuario. */
import type { BackgroundId } from '../../shared/types.ts'
import { BACKGROUNDS } from '../canvas/background.ts'
import type { App } from '../app.ts'
import { h, openDialog, toast } from './dom.ts'
import { icon } from './icons.ts'
import { TEMPLATES } from './templates.ts'

export function openShareDialog(app: App): void {
  openDialog((close) => {
    const linkInput = h('input', { class: 'input', readonly: true, value: '', placeholder: 'Generando enlace…' }) as HTMLInputElement
    let role: 'edit' | 'view' = 'edit'
    let copyBtn: HTMLButtonElement

    const rolePills = h('div', { class: 'role-pills' })
    /**
     * Mientras llega el enlace del rol elegido, el campo queda vacío y "Copiar"
     * desactivado: antes, con red lenta, se podía copiar el enlace de EDICIÓN
     * con "Solo ver" marcado, y una respuesta tardía pisaba la otra.
     */
    const setRole = (next: 'edit' | 'view'): void => {
      role = next
      for (const pill of Array.from(rolePills.children)) {
        pill.classList.toggle('is-active', (pill as HTMLElement).dataset.role === role)
      }
      linkInput.value = ''
      linkInput.placeholder = 'Generando enlace…'
      if (copyBtn) copyBtn.disabled = true
      app
        .createShareLink(next)
        .then((url) => {
          if (next !== role) return
          linkInput.value = url
          copyBtn.disabled = false
        })
        .catch(() => {
          if (next !== role) return
          linkInput.placeholder = 'No se pudo generar el enlace. Vuelve a intentarlo.'
        })
    }
    rolePills.append(
      h('button', { class: 'pill is-active', 'data-role': 'edit', onclick: () => setRole('edit') }, [
        'Puede editar',
      ]),
      h('button', { class: 'pill', 'data-role': 'view', onclick: () => setRole('view') }, ['Solo ver']),
    )

    copyBtn = h(
      'button',
      {
        class: 'btn-primary',
        disabled: true,
        onclick: async () => {
          if (!linkInput.value) return
          try {
            await navigator.clipboard.writeText(linkInput.value)
            toast('Enlace copiado')
          } catch {
            linkInput.select()
            document.execCommand('copy')
            toast('Enlace copiado')
          }
        },
      },
      ['Copiar'],
    ) as HTMLButtonElement

    // Genera de entrada un enlace de invitación con permiso de edición.
    setRole('edit')

    return h('div', {}, [
      h('h2', {}, ['Compartir pizarra']),
      h('p', { class: 'sub' }, [
        'Cualquiera con el enlace puede abrir esta pizarra y colaborar en tiempo real, sin cuenta.',
      ]),
      rolePills,
      h('div', { class: 'copy-row', style: 'margin-top:12px' }, [linkInput, copyBtn]),
      h('p', { class: 'sub', style: 'margin-top:16px' }, [
        'Consejo: abre este mismo enlace en el iPad y escribe con el Apple Pencil; lo que dibujes aparece aquí al instante.',
      ]),
      h('div', { class: 'dialog-actions' }, [
        h('button', { class: 'btn-secondary', onclick: () => close() }, ['Listo']),
      ]),
    ])
  })
}

export function openBackgroundDialog(app: App): void {
  openDialog((close) => {
    const grid = h('div', { class: 'bg-grid' })
    for (const bg of Object.values(BACKGROUNDS)) {
      const card = h(
        'button',
        {
          class: `bg-card ${app.board.background === bg.id ? 'is-selected' : ''}`,
          style: `background:${bg.fill}`,
          onclick: () => {
            app.setBackground(bg.id as BackgroundId)
            close()
          },
        },
        [h('span', {}, [bg.label])],
      )
      if (bg.pattern !== 'none') {
        card.style.backgroundImage =
          bg.pattern === 'dots'
            ? `radial-gradient(${bg.patternColor} 1.2px, transparent 1.2px)`
            : bg.pattern === 'grid'
              ? `linear-gradient(${bg.patternColor} 1px, transparent 1px), linear-gradient(90deg, ${bg.patternColor} 1px, transparent 1px)`
              : `linear-gradient(${bg.patternColor} 1px, transparent 1px)`
        card.style.backgroundSize = '14px 14px'
        card.style.backgroundColor = bg.fill
      }
      grid.append(card)
    }
    return h('div', {}, [
      h('h2', {}, ['Formato del fondo']),
      h('p', { class: 'sub' }, ['El fondo se aplica para todos los que abran la pizarra.']),
      grid,
      h('div', { class: 'dialog-actions' }, [
        h('button', { class: 'btn-secondary', onclick: () => close() }, ['Cerrar']),
      ]),
    ])
  })
}

export function openTemplatesDialog(app: App): void {
  openDialog((close) => {
    const grid = h(
      'div',
      { class: 'template-grid' },
      TEMPLATES.map((tpl) =>
        h(
          'button',
          {
            class: 'template-card',
            onclick: () => {
              app.insertTemplate(tpl.id)
              close()
            },
          },
          [
            h('div', { html: templatePreview(tpl.id) }),
            h('div', { class: 'name' }, [tpl.name]),
            h('div', { class: 'desc' }, [tpl.description]),
          ],
        ),
      ),
    )
    return h('div', {}, [
      h('h2', {}, ['Plantillas']),
      h('p', { class: 'sub' }, ['Se insertan en el centro de la vista actual.']),
      grid,
      h('div', { class: 'dialog-actions' }, [
        h('button', { class: 'btn-secondary', onclick: () => close() }, ['Cerrar']),
      ]),
    ])
  })
}

function templatePreview(id: string): string {
  const bg = '#F3F2F1'
  const shapes: Record<string, string> = {
    brainstorm:
      '<rect x="6" y="8" width="18" height="18" rx="2" fill="#FEF3A3"/><rect x="28" y="8" width="18" height="18" rx="2" fill="#C9E7FF"/><rect x="50" y="8" width="18" height="18" rx="2" fill="#D6F0BE"/><rect x="6" y="30" width="18" height="18" rx="2" fill="#FFD1E3"/><rect x="28" y="30" width="18" height="18" rx="2" fill="#FEF3A3"/><rect x="50" y="30" width="18" height="18" rx="2" fill="#C9E7FF"/>',
    kanban:
      '<rect x="5" y="6" width="20" height="44" rx="3" fill="#fff" stroke="#D1D1D1"/><rect x="5" y="6" width="20" height="8" rx="3" fill="#FFD1E3"/><rect x="28" y="6" width="20" height="44" rx="3" fill="#fff" stroke="#D1D1D1"/><rect x="28" y="6" width="20" height="8" rx="3" fill="#C9E7FF"/><rect x="51" y="6" width="20" height="44" rx="3" fill="#fff" stroke="#D1D1D1"/><rect x="51" y="6" width="20" height="8" rx="3" fill="#D6F0BE"/>',
    retro:
      '<rect x="5" y="6" width="20" height="44" rx="3" fill="#D6F0BE"/><rect x="28" y="6" width="20" height="44" rx="3" fill="#FEF3A3"/><rect x="51" y="6" width="20" height="44" rx="3" fill="#C9E7FF"/>',
    standup:
      '<rect x="5" y="10" width="20" height="36" rx="3" fill="#E0D5FA"/><rect x="28" y="10" width="20" height="36" rx="3" fill="#C3F0E7"/><rect x="51" y="10" width="20" height="36" rx="3" fill="#FFD8A8"/>',
    matrix:
      '<rect x="8" y="6" width="60" height="44" fill="#fff" stroke="#B0B0B0"/><path d="M38 6v44M8 28h60" stroke="#B0B0B0"/>',
    week: '<rect x="4" y="8" width="12" height="40" rx="2" fill="#C9E7FF"/><rect x="18" y="8" width="12" height="40" rx="2" fill="#C9E7FF"/><rect x="32" y="8" width="12" height="40" rx="2" fill="#C9E7FF"/><rect x="46" y="8" width="12" height="40" rx="2" fill="#C9E7FF"/><rect x="60" y="8" width="12" height="40" rx="2" fill="#C9E7FF"/>',
  }
  return `<svg viewBox="0 0 76 56" width="100%" height="72" style="background:${bg};border-radius:4px">${shapes[id] ?? ''}</svg>`
}

export function openShortcutsDialog(): void {
  const rows: [string, string][] = [
    ['Seleccionar', 'Alt + S'],
    ['Mover el lienzo (mano)', 'H'],
    ['Lazo', 'Alt + Q'],
    ['Pluma', 'Alt + W'],
    ['Resaltador', 'Alt + H'],
    ['Borrador', 'Alt + X'],
    ['Puntero láser', 'Alt + L'],
    ['Nota adhesiva', 'N'],
    ['Texto', 'T'],
    ['Figura', 'R'],
    ['Mejorar forma dibujada', 'Alt + B'],
    ['Deshacer', 'Ctrl + Z'],
    ['Rehacer', 'Ctrl + Y'],
    ['Seleccionar todo', 'Ctrl + A'],
    ['Duplicar', 'Ctrl + D'],
    ['Copiar / Pegar', 'Ctrl + C / V'],
    ['Eliminar', 'Supr'],
    ['Traer al frente', 'Ctrl + Shift + ]'],
    ['Enviar al fondo', 'Ctrl + Shift + ['],
    ['Acercar / alejar', 'Alt + + / −'],
    ['Ajustar a la pantalla', 'Shift + 1'],
    ['Zoom al 100 %', 'Shift + 0'],
    ['Mover el lienzo sin cambiar de herramienta', 'Espacio + arrastrar'],
    ['Editar texto', 'Enter'],
    ['Cancelar / deseleccionar', 'Esc'],
  ]
  openDialog((close) =>
    h('div', {}, [
      h('h2', {}, ['Atajos de teclado']),
      h('p', { class: 'sub' }, ['En Mac usa ⌘ en lugar de Ctrl.']),
      h(
        'div',
        { class: 'shortcut-list' },
        rows.map(([label, keys]) => h('div', {}, [h('span', {}, [label]), h('kbd', {}, [keys])])),
      ),
      h('div', { class: 'dialog-actions' }, [
        h('button', { class: 'btn-secondary', onclick: () => close() }, ['Cerrar']),
      ]),
    ]),
  )
}

export function openNameDialog(current: string, onSave: (name: string) => void): void {
  openDialog((close) => {
    const input = h('input', {
      class: 'input',
      value: current,
      maxlength: 40,
      placeholder: 'Tu nombre',
    }) as HTMLInputElement
    const save = (): void => {
      const value = input.value.trim()
      if (value) onSave(value)
      close()
    }
    input.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') save()
      e.stopPropagation()
    })
    return h('div', { class: 'login-card' }, [
      h('h2', {}, ['¿Cómo te llamas?']),
      h('p', { class: 'sub' }, ['Así te verán los demás mientras colaboran contigo.']),
      h('div', { class: 'field' }, [input]),
      h('div', { class: 'dialog-actions' }, [
        h('button', { class: 'btn-primary', onclick: save }, ['Guardar']),
      ]),
    ])
  })
}

export function openLinkDialog(onInsert: (url: string, label: string) => void): void {
  openDialog((close) => {
    const url = h('input', { class: 'input', placeholder: 'https://…' }) as HTMLInputElement
    const label = h('input', { class: 'input', placeholder: 'Texto a mostrar (opcional)' }) as HTMLInputElement
    const save = (): void => {
      const value = url.value.trim()
      if (!value) return close()
      onInsert(value, label.value.trim() || value)
      close()
    }
    for (const input of [url, label]) {
      input.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') save()
        e.stopPropagation()
      })
    }
    return h('div', {}, [
      h('h2', {}, ['Insertar enlace']),
      h('div', { class: 'field' }, [h('label', {}, ['Dirección']), url]),
      h('div', { class: 'field' }, [h('label', {}, ['Texto']), label]),
      h('div', { class: 'dialog-actions' }, [
        h('button', { class: 'btn-secondary', onclick: () => close() }, ['Cancelar']),
        h('button', { class: 'btn-primary', onclick: save }, ['Insertar']),
      ]),
    ])
  })
}

export function openConfirmDialog(options: {
  title: string
  message: string
  confirmLabel: string
  onConfirm: () => void
}): void {
  openDialog((close) =>
    h('div', {}, [
      h('h2', {}, [options.title]),
      h('p', { class: 'sub' }, [options.message]),
      h('div', { class: 'dialog-actions' }, [
        h('button', { class: 'btn-secondary', onclick: () => close() }, ['Cancelar']),
        h(
          'button',
          {
            class: 'btn-primary',
            style: 'background:var(--danger)',
            onclick: () => {
              options.onConfirm()
              close()
            },
          },
          [options.confirmLabel],
        ),
      ]),
    ]),
  )
}

export function exportIcon(): string {
  return icon('download')
}
